import { Globe, Linkedin, Mail, Phone } from "lucide-react";
import {
  useGetIdentity,
  useLocaleState,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { DeleteButton } from "@/components/admin/delete-button";
import { ShowButton } from "@/components/admin/show-button";
import { TextField } from "@/components/admin/text-field";
import { UrlField } from "@/components/admin/url-field";
import { SelectField } from "@/components/admin/select-field";

import { CustomFieldsAside, FieldRow } from "../customFields/CustomFields";
import { formatCurrency } from "../customFields/format";
import { ChoiceBadge, ChoiceLabels } from "../misc/ChoiceBadge";
import { RatingStars } from "../misc/Rating";
import { isEmptyValue } from "../misc/isEmptyValue";
import { ToDefine } from "../misc/ToDefine";
import { formatLocalizedDate } from "../misc/RelativeDate";
import { AsideSection } from "../misc/AsideSection";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";
import { getTranslatedCompanySizeLabel } from "./getTranslatedCompanySizeLabel";
import { sizes } from "./sizes";
import { useGetSalesName } from "../sales/useGetSalesName";
import { CreateDealButton } from "../deals/CreateDealButton";
import { CompanySendEmailButton } from "../mail/RecordEmailWidgets";
import { AddEvent } from "../events/AddEvent";
import { EventsIterator } from "../events/EventsIterator";
import { AddTask } from "../tasks/AddTask";
import { TasksIterator } from "../tasks/TasksIterator";

interface CompanyAsideProps {
  link?: string;
}

export const CompanyAside = ({ link = "edit" }: CompanyAsideProps) => {
  const record = useRecordContext<Company>();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <div className="hidden sm:block w-92 min-w-92 space-y-4">
      <div className="flex flex-wrap gap-1">
        {link === "edit" ? (
          <>
            <EditButton label={translate("resources.companies.action.edit")} />
            <CreateDealButton companyId={record.id} />
            <CompanySendEmailButton company={record} />
          </>
        ) : (
          <ShowButton label={translate("resources.companies.action.show")} />
        )}
      </div>

      <ProspectionInfo record={record} />

      <CompanyTasksSection />

      <CompanyEventsSection />

      <CompanyInfo record={record} />

      <AddressInfo record={record} />

      <ContextInfo record={record} />

      <AdditionalInfo record={record} />

      <CustomFieldsAside resource="companies" values={record.custom_fields} />

      {link !== "edit" && (
        <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
          <DeleteButton
            className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
            size="sm"
          />
        </div>
      )}
    </div>
  );
};

/** Tasks of the company, with a button to add one. */
export const CompanyTasksSection = () => {
  const translate = useTranslate();
  return (
    <AsideSection title={translate("resources.tasks.name", { smart_count: 2 })}>
      <ReferenceManyField
        target="company_id"
        reference="tasks"
        sort={{ field: "due_date", order: "ASC" }}
        perPage={1000}
      >
        <TasksIterator />
      </ReferenceManyField>
      <AddTask target="company" />
    </AsideSection>
  );
};

/** Events booked for the company, latest first, with a button to add one. */
export const CompanyEventsSection = () => {
  const translate = useTranslate();
  return (
    <AsideSection
      title={translate("resources.events.name", { smart_count: 2 })}
    >
      <ReferenceManyField
        target="company_id"
        reference="events"
        sort={{ field: "start_at", order: "DESC" }}
        perPage={100}
      >
        <EventsIterator />
      </ReferenceManyField>
      <AddEvent target="company" />
    </AsideSection>
  );
};

/** Prospecting fields, always shown: an empty field reads « À définir ». */
export const ProspectionInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const {
    acquisitionChannels,
    currency,
    customerStatuses,
    eventTypes,
    prospectStates,
  } = useConfigurationContext();
  const label = (field: string) =>
    translate(`resources.companies.fields.${field}`);

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.prospection")}
    >
      <FieldRow label={label("customer_status")}>
        <ChoiceBadge
          choices={customerStatuses}
          value={record.customer_status}
        />
      </FieldRow>
      <FieldRow label={label("prospect_state")}>
        <ChoiceBadge choices={prospectStates} value={record.prospect_state} />
      </FieldRow>
      <FieldRow label={label("lead_score")}>
        <RatingStars value={record.lead_score} />
      </FieldRow>
      <FieldRow label={label("event_types")}>
        <ChoiceLabels choices={eventTypes} value={record.event_types} />
      </FieldRow>
      <FieldRow label={label("acquisition_channel")}>
        <ChoiceLabels
          choices={acquisitionChannels}
          value={record.acquisition_channel}
        />
      </FieldRow>
      <FieldRow label={label("budget")}>
        {isEmptyValue(record.budget) ? (
          <ToDefine />
        ) : (
          formatCurrency(record.budget, locale, currency)
        )}
      </FieldRow>
      <FieldRow label={label("distance_km")}>
        {isEmptyValue(record.distance_km) ? (
          <ToDefine />
        ) : (
          `${new Intl.NumberFormat(locale).format(Number(record.distance_km))} km`
        )}
      </FieldRow>
      <FieldRow label={label("last_contact_date")}>
        {record.last_contact_date ? (
          formatLocalizedDate(record.last_contact_date, locale)
        ) : (
          <ToDefine />
        )}
      </FieldRow>
    </AsideSection>
  );
};

export const CompanyInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  if (
    !record.website &&
    !record.linkedin_url &&
    !record.phone_number &&
    !record.email
  ) {
    return null;
  }

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.contact")}
    >
      {record.website && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Globe className="w-4 h-4" />
          <UrlField
            source="website"
            target="_blank"
            rel="noopener"
            content={record.website
              .replace("http://", "")
              .replace("https://", "")}
          />
        </div>
      )}
      {record.linkedin_url && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Linkedin className="w-4 h-4" />
          <a
            className="underline hover:no-underline"
            href={record.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            title={record.linkedin_url}
          >
            LinkedIn
          </a>
        </div>
      )}
      {record.phone_number && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Phone className="w-4 h-4" />
          <TextField source="phone_number" />
        </div>
      )}
      {record.email && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Mail className="w-4 h-4" />
          <a
            className="underline hover:no-underline"
            href={`mailto:${record.email}`}
          >
            {record.email}
          </a>
        </div>
      )}
    </AsideSection>
  );
};

export const ContextInfo = ({ record }: { record: Company }) => {
  const { companySectors } = useConfigurationContext();
  const translate = useTranslate();
  if (!record.revenue && !record.id) {
    return null;
  }

  const sector = companySectors.find((s) => s.value === record.sector);
  const sectorLabel = sector?.label;
  const translatedSizes = sizes.map((size) => ({
    ...size,
    name: getTranslatedCompanySizeLabel(size, translate),
  }));

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.context")}
    >
      {sectorLabel && (
        <span>
          {translate("resources.companies.fields.sector")}: {sectorLabel}
        </span>
      )}
      {record.size && (
        <span>
          {translate("resources.companies.fields.size")}:{" "}
          <SelectField source="size" choices={translatedSizes} />
        </span>
      )}
      {record.revenue && (
        <span>
          {translate("resources.companies.fields.revenue")}:{" "}
          <TextField source="revenue" />
        </span>
      )}
      {record.tax_identifier && (
        <span>
          {translate("resources.companies.fields.tax_identifier", {})}
          : <TextField source="tax_identifier" />
        </span>
      )}
    </AsideSection>
  );
};

export const AddressInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  if (
    !record.address &&
    !record.city &&
    !record.zipcode &&
    !record.state_abbr
  ) {
    return null;
  }

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.address")}
      noGap
    >
      <TextField source="address" />
      <TextField source="city" />
      <TextField source="zipcode" />
      <TextField source="state_abbr" />
      <TextField source="country" />
    </AsideSection>
  );
};

export const AdditionalInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  const [locale = "en"] = useLocaleState();
  const { identity } = useGetIdentity();
  const isCurrentUser = record.sales_id === identity?.id;
  const salesName = useGetSalesName(record.sales_id, {
    enabled: record.sales_id != null && !isCurrentUser,
  });
  if (
    !record.created_at &&
    !record.sales_id &&
    !record.description &&
    !record.context_links
  ) {
    return null;
  }
  const getBaseURL = (url: string) => {
    const urlObject = new URL(url.startsWith("http") ? url : `https://${url}`);
    return urlObject.hostname;
  };

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.additional_info")}
    >
      {record.description && (
        <p className="text-sm  mb-1">{record.description}</p>
      )}
      {record.context_links && (
        <div className="flex flex-col">
          {record.context_links.map((link, index) =>
            link ? (
              <a
                key={index}
                className="text-sm underline hover:no-underline mb-1"
                href={link.startsWith("http") ? link : `https://${link}`}
                target="_blank"
                rel="noopener noreferrer"
                title={link}
              >
                {getBaseURL(link)}
              </a>
            ) : null,
          )}
        </div>
      )}
      {record.sales_id == null ? (
        <div className="inline-flex text-sm text-muted-foreground mb-1">
          {translate("resources.companies.followed_by", {
            name: translate("crm.common.to_define"),
          })}
        </div>
      ) : (
        <div className="inline-flex text-sm text-muted-foreground mb-1">
          {translate(
            isCurrentUser
              ? "resources.companies.followed_by_you"
              : "resources.companies.followed_by",
            { name: salesName },
          )}
        </div>
      )}
      {record.created_at && (
        <p className="text-sm text-muted-foreground mb-1">
          {translate("resources.companies.added_on", {
            date: formatLocalizedDate(record.created_at, locale),
          })}{" "}
        </p>
      )}
    </AsideSection>
  );
};
